import { buildApiUrl, apiConfig } from "../config/api";

export { apiConfig, buildApiUrl };

export type MultipartFieldValue = Blob | string | null | undefined;
export type MultipartFields = Record<
  string,
  MultipartFieldValue | MultipartFieldValue[]
>;

export type MultipartRequestOptions<T> = {
  fields: MultipartFields;
  onError?: (response: Response, errorMessage: string) => void;
  url: string;
};

export function getHttpStatusMessage(response: Response): string {
  return `HTTP ${response.status}: ${response.statusText}`;
}

export async function getApiErrorMessage(response: Response): Promise<string> {
  const fallback = getHttpStatusMessage(response);
  const errorText = await response.text();

  if (!errorText) {
    return fallback;
  }

  try {
    const parsedError = JSON.parse(errorText) as unknown;
    if (
      parsedError &&
      typeof parsedError === "object" &&
      "detail" in parsedError &&
      typeof parsedError.detail === "string"
    ) {
      return parsedError.detail;
    }
  } catch {
    return errorText;
  }

  return fallback;
}

export function buildMultipartFormData(fields: MultipartFields): FormData {
  const formData = new FormData();
  Object.entries(fields).forEach(([name, value]) => {
    const values = Array.isArray(value) ? value : [value];
    values.forEach((fieldValue) => {
      if (fieldValue !== null && fieldValue !== undefined) {
        formData.append(name, fieldValue);
      }
    });
  });
  return formData;
}

export async function postMultipart<T>({
  fields,
  onError,
  url,
}: MultipartRequestOptions<T>): Promise<{ data: T; response: Response }> {
  const response = await fetch(url, {
    method: "POST",
    body: buildMultipartFormData(fields),
  });

  if (!response.ok) {
    const errorMessage = await getApiErrorMessage(response);
    onError?.(response, errorMessage);
    throw new Error(errorMessage);
  }

  return {
    response,
    data: (await response.json()) as T,
  };
}
